import { useEffect, useRef, useState } from "react";
import { Button as Btn, Input, Select, Upload } from "antd";
import {
  CalendarOutlined,
  LoadingOutlined,
  ReloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

import { useTranslation } from "contexts/TranslationContext";
import {
  useGetUserAppsQuery,
  useGetInstlmntDashboardMutation,
  useInsertAppRestructureMutation,
  useInsertAppTopupMutation,
  useInsertAppBuyoutMutation,
  useInsertAppSettlementMutation,
  useInsertAppAdhocChargeMutation,
  useInsertAppPartialSettlementMutation,
  useGetSettlementQuoteMutation,
  useGetNocLetterMutation,
} from "services/auth";
import routes, { appRoute } from "shared/constants/routes";
import env from "shared/constants/env";
import formatDateDDMMYYYY from "utilities/formatDate";
import { base64ToPdf, revokePdfUrl } from "utilities/pdfDocument";
import SaudiRiyal from "assets/svg/saudi_riyal_symbol.svg";
import VehicleSuccessIcon from "assets/icons/VehicleSuccess.svg";
import {
  FLOW_TYPE,
  PARTIAL_REPAYMENT_TYPES,
  PARTIAL_RECALCULATION_METHODS,
} from "components/Home/OtherServices/otherServicesData";
import ServiceEmptyState from "./ServiceEmptyState";
import SubmitSuccessModal from "./SubmitSuccessModal";
import DocumentResultCard from "./DocumentResultCard";

// Available restructure/topup tenures (months). Hardcoded until an API provides
// them — swap for real options when the backend is ready.
const TENURE_MONTHS = [6, 12, 18, 24, 30, 36, 48, 60];

// Per-flow UI and behavior for the shared Other Services journey.
const FLOW_CONFIG = {
  [FLOW_TYPE.RESTRUCTURE]: {
    reviewTitleKey: "OTHER_SERVICES.REVIEW_RESTRUCTURE",
    needsContract: true,
    showContractSummary: true,
    showTenure: true,
    showDocumentUpload: true,
    showTopupAmount: false,
  },
  [FLOW_TYPE.TOPUP]: {
    reviewTitleKey: "OTHER_SERVICES.REVIEW_TOPUP",
    needsContract: true,
    showContractSummary: true,
    showTenure: true,
    showDocumentUpload: true,
    showTopupAmount: true,
  },
  // Buy-Out has no application picker and no contract lookup — the debt sits at
  // another bank. Two own steps: the other bank's details, then the customer's
  // extra amount / IBAN / tenure. Creates an application like restructure/topup.
  [FLOW_TYPE.BUYOUT]: {
    isBuyout: true,
    debtTitleKey: "OTHER_SERVICES.BUYOUT_DEBT_TITLE",
    reviewTitleKey: "OTHER_SERVICES.REVIEW_BUYOUT",
    showTenure: true,
    showExtraAmount: true,
    showIban: true,
    showDocumentUpload: true,
  },
  [FLOW_TYPE.EARLY_SETTLEMENT]: {
    reviewTitleKey: "OTHER_SERVICES.REVIEW_EARLY_SETTLEMENT",
    isSettlementRequest: true,
    needsContract: true,
    showContractSummary: true,
    showEarlySettlementAmount: true,
    successTitleKey: "OTHER_SERVICES.SERVICING_SUCCESS_TITLE",
    successMessageKey: "OTHER_SERVICES.SERVICING_SUCCESS_MSG",
  },
  [FLOW_TYPE.CANCELLATION]: {
    formTitleKey: "OTHER_SERVICES.REVIEW_FINANCE_DETAILS",
    reviewTitleKey: "OTHER_SERVICES.REVIEW_CANCELLATION",
    isSettlementRequest: true,
    needsContract: true,
    showContractSummary: true,
    successTitleKey: "OTHER_SERVICES.SERVICING_SUCCESS_TITLE",
    successMessageKey: "OTHER_SERVICES.SERVICING_SUCCESS_MSG",
  },
  // Partial settlement collects its own three fields; sourceOfIncome is derived
  // from the chosen repaymentType at submit time.
  [FLOW_TYPE.PARTIAL_SETTLEMENT]: {
    reviewTitleKey: "OTHER_SERVICES.REVIEW_PARTIAL_SETTLEMENT",
    isServicingRequest: true,
    isPartialSettlement: true,
    needsContract: true,
    showContractSummary: true,
    showPartialFields: true,
    successTitleKey: "OTHER_SERVICES.SERVICING_SUCCESS_TITLE",
    successMessageKey: "OTHER_SERVICES.SERVICING_SUCCESS_MSG",
  },
  [FLOW_TYPE.ADHOC_CHARGE]: {
    isAdhocCharge: true,
    successTitleKey: "OTHER_SERVICES.ADHOC_SUCCESS_TITLE",
    successMessageKey: "OTHER_SERVICES.ADHOC_SUCCESS_MSG",
    successImage: VehicleSuccessIcon,
  },
  [FLOW_TYPE.CLEARANCE_LETTER]: {
    isDocumentFlow: true,
    readyTitleKey: "OTHER_SERVICES.REVIEW_CLEARANCE_LETTER",
    pendingTitleKey: "OTHER_SERVICES.DOCUMENT_PENDING_TITLE",
    pendingMessageKey: "OTHER_SERVICES.DOCUMENT_PENDING_MSG",
  },
};

// Backend error codes → translation keys. Covers the shared middleware
// (rate limiter, atomic lock) and the per-route failures of every Other
// Services endpoint. Anything unmapped falls back to the server message.
const ERROR_MESSAGE_KEYS = {
  ACTIVE_APPLICATION: "ACTIVE_APPLICATION",
  APPLICATION_EXISTS: "ACTIVE_APPLICATION",
  APPLICATION_NOT_FOUND: "OTHER_SERVICES.ERR_APPLICATION_NOT_FOUND",
  PREVIOUS_APPLICATION_NOT_FOUND: "OTHER_SERVICES.ERR_APPLICATION_NOT_FOUND",
  APPLICATION_FORBIDDEN: "OTHER_SERVICES.ERR_APPLICATION_FORBIDDEN",
  PREVIOUS_APPLICATION_FORBIDDEN: "OTHER_SERVICES.ERR_APPLICATION_FORBIDDEN",
  OPERATION_IN_PROGRESS: "OTHER_SERVICES.ERR_OPERATION_IN_PROGRESS",
  RATE_LIMIT_EXCEEDED: "OTHER_SERVICES.ERR_RATE_LIMIT",
  FINANCE_CANCELLATION_NOT_AVAILABLE: "OTHER_SERVICES.CANCELLATION_NOT_AVAILABLE_IJARAH",
  UNSUPPORTED_SETTLEMENT_PRODUCT: "OTHER_SERVICES.ERR_REQUEST_FAILED",
  SETTLEMENT_REQUEST_FAILED: "OTHER_SERVICES.ERR_REQUEST_FAILED",
  EARLY_SETTLEMENT_REQUEST_FAILED: "OTHER_SERVICES.ERR_REQUEST_FAILED",
  PARTIAL_SETTLEMENT_REQUEST_FAILED: "OTHER_SERVICES.ERR_REQUEST_FAILED",
  SETTLEMENT_QUOTE_FAILED: "OTHER_SERVICES.ERR_REQUEST_FAILED",
  LOAN_CANCELLATION_REQUEST_FAILED: "OTHER_SERVICES.ERR_REQUEST_FAILED",
  EARLY_SETTLEMENT_INQUIRY_FAILED: "OTHER_SERVICES.ERR_REQUEST_FAILED",
  ADHOC_CHARGES_REQUEST_FAILED: "OTHER_SERVICES.ERR_REQUEST_FAILED",
  NOC_REQUEST_FAILED: "OTHER_SERVICES.ERR_REQUEST_FAILED",
};

const fmt = (n) => (n == null ? "-" : Number(n).toLocaleString());
const parseAmount = (val) => parseFloat(String(val ?? "0").replace(/,/g, "")) || 0;
const isIjarahProductRef = (productRef) => String(productRef || "").startsWith("SMB.TA.IJR");

// Derive the contract summary from the V2 view-contract / installment response
// (same source as Settings › Installment Details). These services are V2-only,
// so we read the Temenos header/body directly.
const extractContractInfo = (res) => {
  const header = res?.header?.data || {};
  const outstanding =
    parseAmount(header.principalOverdue) + parseAmount(header.outstandingProfit);
  const body = res?.body || [];
  const pending = body.filter((i) => String(i.Status).toUpperCase() !== "PAID");
  const nextDue = pending
    .map((i) => new Date(i.paymentDate))
    // .filter((d) => !isNaN(d.getTime()) && d >= new Date())
    .sort((a, b) => a - b)[0];
  return {
    outstanding,
    contractAmount: parseAmount(header.disbursementAmount),
    paidAmount: parseAmount(header.principalPaid),
    remainingTenure: pending.length,
    nextPaymentDue: nextDue || null,
  };
};

// Upload the selected document to the created application (Restructure/Topup
// only — Early Settlement has no upload step) — mirrors the V2 product upload
// (multipart POST to app/upload-document with the app id; axios bypasses the
// window.fetch auth interceptor, so set the token manually).
const uploadServiceDocument = async (file, appId) => {
  const formData = new FormData();
  formData.append("document", file);
  formData.append("appId", appId);
  formData.append("evidenceType", "OtherDocuments");
  formData.append(
    "forRequirements",
    "SupportingFinancialDocuments-OtherDocuments"
  );
  const stored = JSON.parse(localStorage.getItem("@@TAMWEEL_USER") || "null");
  await axios.post(`${env.API_ENDPOINT_V2}app/upload-document`, formData, {
    headers: {
      Authorization: `Bearer ${stored?.token}`,
      "Content-Type": "multipart/form-data",
    },
  });
};

// Headline amount in a tinted card — the outstanding balance on the review
// step, and the cancelation fee on Finance Cancelation.
const AmountCard = ({ label, value }) => (
  <div className="bg-[#EAF6F7] rounded-xl p-5 mb-5">
    <p className="text-[#5A7D82] text-sm mb-1">{label}</p>
    <p className="text-primary text-2xl md:text-3xl font-bold flex items-center gap-2">
      {fmt(value)}
      <img src={SaudiRiyal} alt="" className="w-5 h-5" />
    </p>
  </div>
);

// Read-only contract field (grey box with optional leading icon).
const ReadOnlyField = ({ label, icon, children }) => (
  <div className="mb-4">
    <label className="block text-[#5F5F5F] text-sm mb-1">{label}</label>
    <div className="bg-[#F5F5F5] rounded-lg px-4 py-3 flex items-center gap-2 text-[#2F2F2F] text-sm md:text-base">
      {icon}
      <span>{children}</span>
    </div>
  </div>
);

// Single-PDF picker. The file is held locally until submit — shared by the flows
// that upload to the created application and by Buy-Out (UI only, no API yet).
const DocumentUploadField = ({ label, hint, placeholder, uploadText, file, onPick }) => (
  <div className="mt-4">
    <label className="block text-[#5F5F5F] text-sm mb-1">
      {label} <span className="text-[#8E9196] text-xs">{hint}</span>{" "}
      <span className="text-red-500">*</span>
    </label>
    <div className="border border-dashed border-[#C8CDD2] rounded-lg p-3 flex items-center justify-between gap-3">
      <span className="text-[#8E9196] text-sm truncate">{file ? file.name : placeholder}</span>
      <Upload
        accept="application/pdf"
        showUploadList={false}
        maxCount={1}
        beforeUpload={(picked) => {
          onPick(picked);
          return false; // hold the file; no auto-upload
        }}
      >
        <Btn
          className="rounded-full bg-primary text-white font-semibold px-6 border border-primary hover:!border-primary hover:!text-primary"
          icon={<UploadOutlined />}
        >
          {uploadText}
        </Btn>
      </Upload>
    </div>
  </div>
);

// Right-column flow shared by every Other Services product: pick a SIGNED
// application → review its contract details → fill the flow-specific form
// (see FLOW_CONFIG above) → submit → success modal. Document flows
// (Clearance Letter) skip the form and render the returned PDF instead.
const FinanceServiceFlow = ({ detail, isArabic }) => {
  const { translate } = useTranslation();
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("@@TAMWEEL_USER") || "null");
  const flowType = detail?.flow?.type;
  const flowCfg = FLOW_CONFIG[flowType] || {};

  // Buy-Out opens straight on its form — it needs no existing contract, so the
  // application list is never fetched for it.
  const { data: apps, isLoading: appsLoading } = useGetUserAppsQuery(
    user?.IDB_USR_ID,
    { skip: !!flowCfg.isBuyout }
  );
  const [getInstlmnt] = useGetInstlmntDashboardMutation();
  const [insertAppRestructure, { isLoading: restructureSubmitting }] =
    useInsertAppRestructureMutation();
  const [insertAppTopup, { isLoading: topupSubmitting }] =
    useInsertAppTopupMutation();
  const [insertAppBuyout, { isLoading: buyoutSubmitting }] =
    useInsertAppBuyoutMutation();
  const [insertAppSettlement, { isLoading: settlementSubmitting }] =
    useInsertAppSettlementMutation();
  const [insertAppAdhocCharge, { isLoading: adhocSubmitting }] =
    useInsertAppAdhocChargeMutation();
  const [insertAppPartialSettlement, { isLoading: partialSubmitting }] =
    useInsertAppPartialSettlementMutation();
  const [getSettlementQuote] = useGetSettlementQuoteMutation();
  const [getNocLetter] = useGetNocLetterMutation();
  const submitting =
    restructureSubmitting ||
    topupSubmitting ||
    buyoutSubmitting ||
    settlementSubmitting ||
    adhocSubmitting ||
    partialSubmitting;

  // Services sold per territory (3-Month Authorization / Insurance) start on a
  // region step; everything else starts at the application picker.
  const regions = detail?.flow?.regions || [];
  const hasRegionStep = regions.length > 0;

  // "region" | "debt" | "select" | "form"
  const [step, setStep] = useState(
    hasRegionStep ? "region" : flowCfg.isBuyout ? "debt" : "select"
  );
  const [selectedRegionId, setSelectedRegionId] = useState(null);
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [contract, setContract] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [tenure, setTenure] = useState(undefined);
  const [topupAmount, setTopupAmount] = useState("");
  // Payoff quote from the backend inquiry — read-only, never typed by the customer.
  const [earlySettlementAmount, setEarlySettlementAmount] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [repaymentType, setRepaymentType] = useState(undefined);
  const [recalculationMethod, setRecalculationMethod] = useState(undefined);
  const [noOfInstallment, setNoOfInstallment] = useState("");
  // Buy-Out: the debt being bought out (step 1) and the customer's own terms (step 2).
  const [otherBankName, setOtherBankName] = useState("");
  const [otherBankLoanAmount, setOtherBankLoanAmount] = useState("");
  const [otherBankIBAN, setOtherBankIBAN] = useState("");
  const [otherBankTenure, setOtherBankTenure] = useState("");
  const [debtDocumentFile, setDebtDocumentFile] = useState(null);
  const [extraAmount, setExtraAmount] = useState("");
  const [iban, setIban] = useState("");
  const [documentFile, setDocumentFile] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);

  const selectedRegion = regions.find((r) => r.id === selectedRegionId) || null;

  // Document flows (Clearance Letter): the decoded PDF, or a PENDING marker
  // when the letter has been requested but Temenos hasn't generated it yet.
  const [docLoading, setDocLoading] = useState(false);
  const [docPending, setDocPending] = useState(false);
  const [docResult, setDocResult] = useState(null); // { url, size, fileName }

  // Object URLs must be revoked by whoever created them. Kept in a ref so the
  // unmount cleanup always sees the latest one without re-running the effect.
  const docUrlRef = useRef(null);
  const releaseDocument = () => {
    revokePdfUrl(docUrlRef.current);
    docUrlRef.current = null;
    setDocResult(null);
  };
  useEffect(() => () => revokePdfUrl(docUrlRef.current), []);

  // Eligible = V2 applications whose contract is SIGNED (case-normalized).
  // Restructure/Topup are V2-only, so V1 rows from getUserApps are excluded.
  const signedApps = (apps || []).filter(
    (a) =>
      a?.__apiSource === "v2" &&
      String(a?.CONTRACT_STATUS).toUpperCase() === "SIGNED"
  );

  const loadContract = async () => {
    setContractLoading(true);
    try {
      // Selected application's id, sent alongside the user id so the backend
      // scopes the contract/installment lookup to that specific application
      // (covers both Restructure and Topup — they share this call).
      const res = await getInstlmnt({
        id: Number(user?.IDB_USR_ID),
        applId: selectedAppId,
      }).unwrap();
      setContract(extractContractInfo(res));
    } catch (e) {
      console.error("Failed to load contract details:", e);
      setContract(null);
      toast.error(translate("SIGN_IN.SOMETHING_WENT_WRONG") || "Something went wrong.");
    } finally {
      setContractLoading(false);
    }
  };

  // Request the service's document and decode it for display. Deliberately
  // manual (no auto-poll): the backend re-submits the ad-hoc charge request to
  // Temenos on every call, so retrying is the customer's explicit choice.
  const requestDocument = async () => {
    setDocLoading(true);
    setDocPending(false);
    releaseDocument();
    try {
      // Note the envelope: this endpoint takes { appId, id } only — not the
      // idType/idNumber/source/details shape the other services use.
      const res = await getNocLetter({
        appId: selectedAppId,
        id: Number(user?.IDB_USR_ID),
      }).unwrap();

      // Two 200 shapes: "PENDING" (requested, not generated yet) or a payload
      // carrying the base64 PDF.
      const pdf = res?.status === "PENDING" ? null : base64ToPdf(res?.payload?.content);
      if (!pdf) {
        setDocPending(true);
        return;
      }

      docUrlRef.current = pdf.url;
      setDocResult({
        url: pdf.url,
        size: pdf.size,
        // Prefer the name the API reports; fall back to service config + app id.
        fileName:
          res?.payload?.fileName ||
          `${detail?.flow?.documentBaseName || "Document"}-${selectedAppId}.pdf`,
      });
    } catch (ex) {
      reportSubmitError(ex, "Clearance Letter");
      setStep("select");
    } finally {
      setDocLoading(false);
    }
  };

  // Fetch the real payoff amount for the selected application (Early Settlement).
  const loadQuote = async () => {
    setQuoteLoading(true);
    try {
      const res = await getSettlementQuote({
        appId: selectedAppId,
        id: Number(user?.IDB_USR_ID),
      }).unwrap();
      setEarlySettlementAmount(res?.data?.payoffAmount ?? null);
    } catch (ex) {
      console.error("Settlement quote failed:", ex);
      setEarlySettlementAmount(null);
    } finally {
      setQuoteLoading(false);
    }
  };

  // Region step → application step.
  const handleRegionNext = () => {
    if (!selectedRegionId) return;
    setStep("select");
  };

  // Buy-Out step 1 → step 2. Every field describing the other bank's debt is mandatory.
  const canContinueDebt =
    !!otherBankName.trim() &&
    Number(otherBankLoanAmount) > 0 &&
    !!otherBankIBAN.trim() &&
    Number(otherBankTenure) > 0;
  const handleDebtNext = () => {
    if (canContinueDebt) setStep("form");
  };

  const handleApplicationSelect = (app) => {
    if (flowType === FLOW_TYPE.CANCELLATION && isIjarahProductRef(app?.PRODUCT_REF)) {
      setSelectedAppId(null);
      toast.error(translate("OTHER_SERVICES.CANCELLATION_NOT_AVAILABLE_IJARAH"));
      return;
    }
    setSelectedAppId(app.APPLID);
  };

  // Application step → whatever comes next for this family. Ad-hoc charges have
  // nothing to review, so picking an application submits straight away.
  const handleNext = () => {
    if (!selectedAppId) return;
    if (flowCfg.isAdhocCharge) {
      handleSubmit();
      return;
    }
    setStep("form");
    if (flowCfg.isDocumentFlow) {
      requestDocument();
    } else if (flowCfg.needsContract) {
      loadContract();
      if (flowCfg.showEarlySettlementAmount) loadQuote();
    }
  };

  const handleBack = () => {
    // Buy-Out goes back to its own first step, keeping what was typed there.
    if (flowCfg.isBuyout) {
      setStep("debt");
      return;
    }
    setStep("select");
    setContract(null);
    setTenure(undefined);
    setTopupAmount("");
    setEarlySettlementAmount(null);
    setRepaymentType(undefined);
    setRecalculationMethod(undefined);
    setNoOfInstallment("");
    setDocumentFile(null);
    setDocPending(false);
    releaseDocument();
  };

  // Application step → back to the region step (region flows only).
  const handleBackToRegion = () => {
    setStep("region");
    setSelectedAppId(null);
  };

  const validateDocument = (file) => {
    if (file.type !== "application/pdf") {
      toast.error(translate("SETTINGS.ERROR_FILE_TYPE") || "Please upload a PDF file.");
      return false;
    }
    if (file.size / 1024 / 1024 >= 3) {
      toast.error(translate("SETTINGS.ERROR_FILE_SIZE_3MB") || "File must be under 3 MB.");
      return false;
    }
    return true;
  };

  // Shared across all flow types: log + surface a friendly toast for a failed
  // request. Maps the backend's error codes (see ERROR_MESSAGE_KEYS) to
  // localized copy, falling back to the server's own message.
  const reportSubmitError = (ex, context) => {
    console.error(`${context} request failed:`, ex);
    const code = ex?.data?.error?.code;
    const key = ERROR_MESSAGE_KEYS[code];
    if (key) {
      toast.error(translate(key));
      return;
    }
    toast.error(
      ex?.data?.error?.message ||
      ex?.data?.message ||
      translate("SIGN_IN.SOMETHING_WENT_WRONG") ||
      "Something went wrong. Please try again."
    );
  };

  // The servicing endpoints return the raw Temenos body with HTTP 200 even
  // when Temenos itself refused, so a resolved promise is not success on its
  // own — header.status has to be checked.
  const isTemenosSuccess = (res) => res?.header?.status === "success";

  const handleSubmit = async () => {
    const basePayload = {
      idNumber: String(user?.USER_ID),
      idType: String(user?.USER_ID)?.[0] === "1" ? "NATIONALID" : "IQAMAID",
      source: "WEB",
      userWebId: String(user?.IDB_USR_ID),
    };

    if (flowCfg.isSettlementRequest) {
      const payload = {
        ...basePayload,
        details: {
          appId: selectedAppId,
          requestedService:
            flowType === FLOW_TYPE.CANCELLATION
              ? "FINANCE_CANCELLATION"
              : "EARLY_SETTLEMENT",
        },
      };
      try {
        const res = await insertAppSettlement(payload).unwrap();
        if (!isTemenosSuccess(res)) {
          toast.error(translate("OTHER_SERVICES.ERR_REQUEST_FAILED"));
          return;
        }
        setSuccessOpen(true);
      } catch (ex) {
        reportSubmitError(ex, flowType);
      }
      return;
    }

    // Partial Settlement and ad-hoc charges retain their existing Temenos vocabulary.
    if (flowCfg.isServicingRequest || flowCfg.isAdhocCharge) {
      const payload = {
        ...basePayload,
        details: {
          appId: selectedAppId,
          settlementType: "",
          repaymentType: "",
          recalculationMethod: "",
          noOfInstallment: "",
          aaClosureReason: "",
          prPayoffStatus: "",
          simulationReference: "",
          chargeType: "",
          aaId: "",
          extensions: {},
          ...(detail?.flow?.details || {}),
          ...(flowCfg.isAdhocCharge
            ? { chargeType: selectedRegion?.chargeType || detail?.flow?.chargeType }
            : {}),
          ...(flowCfg.isPartialSettlement
            ? {
              repaymentType,
              recalculationMethod,
              noOfInstallment: String(noOfInstallment),
              // Temenos expects a different sourceOfIncome per repayment flavour.
              sourceOfIncome:
                repaymentType === "Compression Repayment" ? "PARTIAL CR API" : "PARTIAL PD API",
            }
            : {}),
        },
      };
      const submitServicing = flowCfg.isAdhocCharge
        ? insertAppAdhocCharge
        : insertAppPartialSettlement;
      try {
        const res = await submitServicing(payload).unwrap();
        if (!isTemenosSuccess(res)) {
          toast.error(translate("OTHER_SERVICES.ERR_REQUEST_FAILED"));
          return;
        }
        setSuccessOpen(true);
      } catch (ex) {
        reportSubmitError(ex, flowType);
      }
      return;
    }

    // Restructure / Topup share the same payload shape and document-upload step.
    // Buy-Out carries the other bank's debt plus the customer's own terms instead.
    const payload = {
      ...basePayload,
      details: flowCfg.isBuyout
        ? {
          ...(extraAmount ? { loanAmount: Number(extraAmount) } : {}),
          tenure: Number(tenure),
          iban,
          otherBankName,
          otherBankIBAN,
          otherBankLoanAmount: Number(otherBankLoanAmount),
          otherBankTenure: Number(otherBankTenure),
        }
        : {
          tenure: String(tenure),
          restructureType: detail?.flow?.restructureType,
          previousLoan: selectedAppId,
          loanReason: "PU1000000004",
          ...(flowType === FLOW_TYPE.TOPUP ? { topupAmount: String(topupAmount) } : {}),
        },
    };
    const createMutations = {
      [FLOW_TYPE.TOPUP]: insertAppTopup,
      [FLOW_TYPE.BUYOUT]: insertAppBuyout,
    };
    const submitMutation = createMutations[flowType] || insertAppRestructure;

    try {
      const res = await submitMutation(payload).unwrap();
      const appId = res?.data?.applicationId;
      // Buy-Out's uploads are UI-only for now — no document endpoint wired yet.
      if (appId && documentFile && !flowCfg.isBuyout) {
        // Non-blocking: a failed upload shouldn't hide a successful submit.
        try {
          await uploadServiceDocument(documentFile, appId);
        } catch (uploadErr) {
          console.error("Document upload failed:", uploadErr);
        }
      }
      setSuccessOpen(true);
    } catch (ex) {
      reportSubmitError(ex, flowType === FLOW_TYPE.TOPUP ? "Topup" : "Restructure");
    }
  };

  const handleContinue = () => {
    setSuccessOpen(false);
    navigate(appRoute(routes.HOME));
  };

  // Only the fields a flow actually renders can block submission — a flow with
  // no inputs (Cancelation) is submittable straight away. The document upload
  // stays optional.
  const canSubmit =
    (!flowCfg.showTenure || !!tenure) &&
    (!flowCfg.showIban || !!iban.trim()) &&
    (!flowCfg.showTopupAmount || Number(topupAmount) > 0) &&
    (!flowCfg.showEarlySettlementAmount || Number(earlySettlementAmount) > 0) &&
    (!flowCfg.showPartialFields ||
      (!!repaymentType && !!recalculationMethod && Number(noOfInstallment) > 0));

  // ── Loading / empty state ──────────────────────────────────────────────────
  if (appsLoading && !flowCfg.isBuyout) {
    return (
      <div className="flex items-center justify-center min-h-[280px]">
        <LoadingOutlined style={{ fontSize: 32, color: "#1F225B" }} />
      </div>
    );
  }

  // Buy-Out needs no existing contract, so the "no applications" state never applies.
  if (signedApps.length === 0 && !flowCfg.isBuyout) {
    return (
      <ServiceEmptyState
        emptyState={detail.emptyState}
        isArabic={isArabic}
        image={detail.emptyStateImage}
      />
    );
  }

  // Rendered by both the application step (ad-hoc charges submit from there)
  // and the review step. Undefined keys fall back to the modal's IVR defaults.
  const successModal = (
    <SubmitSuccessModal
      open={successOpen}
      onContinue={handleContinue}
      titleKey={flowCfg.successTitleKey}
      messageKey={flowCfg.successMessageKey}
      image={flowCfg.successImage}
    />
  );

  // ── Step 0: select a region (per-territory Vehicle Services only) ──────────
  // The chosen region decides the Temenos chargeType sent on submit.
  if (step === "region") {
    return (
      <div>
        <p className="text-[#2F2F2F] text-base md:text-lg font-medium mb-4">
          {translate(detail?.flow?.regionTitleKey || "OTHER_SERVICES.SELECT_REGION")}
        </p>
        <div className="flex flex-col gap-3">
          {regions.map((region) => {
            const active = selectedRegionId === region.id;
            return (
              <button
                key={region.id}
                type="button"
                onClick={() => setSelectedRegionId(region.id)}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-start transition-colors ${active
                    ? "border-primary bg-[#F4FAFB]"
                    : "border-[#E0E0E0] bg-white hover:border-primary"
                  }`}
              >
                <img src={region.icon} alt="" className="w-7 h-7 shrink-0" />
                <span className="text-[#1F1F1F] text-sm md:text-base">
                  {translate(detail?.flow?.regionLabelKey)} {translate(region.labelKey)}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex justify-end mt-8">
          <Btn
            disabled={!selectedRegionId}
            className="rounded-full bg-primary text-white font-semibold px-12 py-5 border border-primary disabled:opacity-60 hover:!border-primary hover:border-solid hover:!text-primary"
            onClick={handleRegionNext}
          >
            {translate("OTHER_SERVICES.NEXT")}
          </Btn>
        </div>
      </div>
    );
  }

  // ── Step 0b: the debt being bought out (Buy-Out only) ─────────────────────
  // All four fields describe the other bank's finance; they go to Temenos as the
  // otherBank* block. The upload is UI only until a document endpoint exists.
  if (step === "debt") {
    return (
      <div>
        <p className="text-[#2F2F2F] text-base md:text-lg font-medium mb-4">
          {translate(flowCfg.debtTitleKey)}
        </p>

        <div className="bg-[#FAFAFA] border border-[#EFEFEF] rounded-xl p-5 flex flex-col gap-4">
          <div>
            <label className="block text-[#5F5F5F] text-sm mb-1">
              {translate("OTHER_SERVICES.ENTITY_ORGANIZATION")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <Input
              value={otherBankName}
              onChange={(e) => setOtherBankName(e.target.value)}
              placeholder={translate("OTHER_SERVICES.ENTITY_PLACEHOLDER")}
              size="large"
            />
          </div>
          <div>
            <label className="block text-[#5F5F5F] text-sm mb-1">
              {translate("OTHER_SERVICES.AMOUNT")} <span className="text-red-500">*</span>
            </label>
            <Input
              value={otherBankLoanAmount}
              onChange={(e) => setOtherBankLoanAmount(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="00000000"
              size="large"
              inputMode="numeric"
              prefix={<img src={SaudiRiyal} alt="" className="w-4 h-4" />}
            />
          </div>
          <div>
            <label className="block text-[#5F5F5F] text-sm mb-1">
              {translate("OTHER_SERVICES.OTHER_BANK_IBAN")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <Input
              value={otherBankIBAN}
              onChange={(e) => setOtherBankIBAN(e.target.value.replace(/[^a-zA-Z0-9]/g, ""))}
              placeholder={translate("OTHER_SERVICES.IBAN_PLACEHOLDER")}
              size="large"
            />
          </div>
          <div>
            <label className="block text-[#5F5F5F] text-sm mb-1">
              {translate("OTHER_SERVICES.OTHER_BANK_TENURE")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <Input
              value={otherBankTenure}
              onChange={(e) => setOtherBankTenure(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder={translate("OTHER_SERVICES.TENURE_MONTHS_PLACEHOLDER")}
              size="large"
              inputMode="numeric"
            />
          </div>
        </div>

        <DocumentUploadField
          label={translate("OTHER_SERVICES.UPLOAD_DOCUMENT")}
          hint={translate("OTHER_SERVICES.UPLOAD_DOC_HINT")}
          placeholder={translate("OTHER_SERVICES.DEBT_PROOF_LETTER")}
          uploadText={translate("OTHER_SERVICES.UPLOAD")}
          file={debtDocumentFile}
          onPick={(file) => validateDocument(file) && setDebtDocumentFile(file)}
        />

        <div className="flex justify-end gap-3 mt-8">
          <Btn
            className="rounded-full bg-[#F0F0F0] text-[#2F2F2F] font-semibold px-10 py-5 border-0"
            onClick={() => navigate(-1)}
          >
            {translate("OTHER_SERVICES.BACK")}
          </Btn>
          <Btn
            disabled={!canContinueDebt}
            className="rounded-full bg-primary text-white font-semibold px-12 py-5 border border-primary disabled:opacity-60 hover:!border-primary hover:border-solid hover:!text-primary"
            onClick={handleDebtNext}
          >
            {translate("OTHER_SERVICES.NEXT")}
          </Btn>
        </div>
      </div>
    );
  }

  // ── Step 1: select an application ──────────────────────────────────────────
  if (step === "select") {
    return (
      <div>
        <p className="text-[#2F2F2F] text-base md:text-lg font-medium mb-4">
          {translate("OTHER_SERVICES.SELECT_APPLICATION")}
        </p>
        <div className="flex flex-col gap-3">
          {signedApps.map((app) => {
            const active = selectedAppId === app.APPLID;
            return (
              <button
                key={app.APPLID}
                type="button"
                onClick={() => handleApplicationSelect(app)}
                className={`flex items-center gap-3 rounded-xl border px-4 py-4 text-start transition-colors ${active
                    ? "border-primary bg-[#F4FAFB]"
                    : "border-[#E0E0E0] bg-white hover:border-primary"
                  }`}
              >
                <span
                  className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${active ? "border-primary" : "border-[#C8CDD2]"
                    }`}
                >
                  {active ? <span className="w-2.5 h-2.5 rounded-full bg-primary" /> : null}
                </span>
                <span className="text-[#1F1F1F] text-sm md:text-base">
                  {translate("OTHER_SERVICES.APPLICATION_ID")}: {app.APPLID}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex justify-end gap-3 mt-8">
          {/* Region flows need a way back to change territory — the page-level
              back button would leave the service entirely. */}
          {hasRegionStep && (
            <Btn
              className="rounded-full bg-[#F0F0F0] text-[#2F2F2F] font-semibold px-10 py-5 border-0"
              onClick={handleBackToRegion}
            >
              {translate("OTHER_SERVICES.BACK")}
            </Btn>
          )}
          <Btn
            disabled={!selectedAppId || submitting}
            className="rounded-full bg-primary text-white font-semibold px-12 py-5 border border-primary disabled:opacity-60 hover:!border-primary hover:border-solid hover:!text-primary"
            onClick={handleNext}
          >
            {submitting ? <LoadingOutlined /> : translate("OTHER_SERVICES.NEXT")}
          </Btn>
        </div>
        {successModal}
      </div>
    );
  }

  // ── Step 2a: document services (Clearance Letter) ──────────────────────────
  // No form and no submit — the letter was requested on "Next"; this step just
  // reports the outcome: the PDF, or a "not ready yet" state with a retry.
  if (flowCfg.isDocumentFlow) {
    if (docLoading) {
      return (
        <div className="flex items-center justify-center min-h-[200px]">
          <LoadingOutlined style={{ fontSize: 28, color: "#1F225B" }} />
        </div>
      );
    }

    return (
      <div>
        <p className="text-[#2F2F2F] text-base md:text-lg font-medium mb-4">
          {translate(docResult ? flowCfg.readyTitleKey : flowCfg.pendingTitleKey)}
        </p>

        {docResult ? (
          <DocumentResultCard
            url={docResult.url}
            fileName={docResult.fileName}
            size={docResult.size}
          />
        ) : (
          <div className="bg-[#F5F7F8] rounded-xl p-5">
            <p className="text-[#5F5F5F] text-sm md:text-base">
              {translate(flowCfg.pendingMessageKey)}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-8">
          <Btn
            className="rounded-full bg-[#F0F0F0] text-[#2F2F2F] font-semibold px-10 py-5 border-0"
            onClick={handleBack}
          >
            {translate("OTHER_SERVICES.BACK")}
          </Btn>
          {docPending && (
            <Btn
              className="rounded-full bg-primary text-white font-semibold px-10 py-5 border border-primary hover:!border-primary hover:border-solid hover:!text-primary"
              icon={<ReloadOutlined />}
              onClick={requestDocument}
            >
              {translate("OTHER_SERVICES.CHECK_AGAIN")}
            </Btn>
          )}
        </div>
      </div>
    );
  }

  // ── Step 2b: review + form ─────────────────────────────────────────────────
  return (
    <div>
      <p className="text-[#2F2F2F] text-base md:text-lg font-medium mb-4">
        {translate(flowCfg.formTitleKey || flowCfg.reviewTitleKey)}
      </p>

      {contractLoading ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <LoadingOutlined style={{ fontSize: 28, color: "#1F225B" }} />
        </div>
      ) : (
        <>
          {/* Contract summary — outstanding balance + read-only details */}
          {flowCfg.showContractSummary && (
            <>
              <AmountCard
                label={translate("OTHER_SERVICES.OUTSTANDING_AMOUNT")}
                value={contract?.outstanding}
              />
              <ReadOnlyField label={translate("OTHER_SERVICES.REMAINING_TENURE")}>
                {contract?.remainingTenure != null
                  ? `${contract.remainingTenure} ${translate("OTHER_SERVICES.MONTHS_SUFFIX")}`
                  : "-"}
              </ReadOnlyField>
              <ReadOnlyField
                label={translate("SETTINGS.CONTRACT_AMOUNT")}
                icon={<img src={SaudiRiyal} alt="" className="w-4 h-4" />}
              >
                {fmt(contract?.contractAmount)}
              </ReadOnlyField>
              <ReadOnlyField
                label={translate("SETTINGS.PAID_AMOUNT")}
                icon={<img src={SaudiRiyal} alt="" className="w-4 h-4" />}
              >
                {fmt(contract?.paidAmount)}
              </ReadOnlyField>
              <ReadOnlyField
                label={translate("SETTINGS.NEXT_PAYMENT_DUE")}
                icon={<CalendarOutlined className="text-primary" />}
              >
                {contract?.nextPaymentDue ? formatDateDDMMYYYY(contract.nextPaymentDue) : "-"}
              </ReadOnlyField>
            </>
          )}

          {/* Extra amount + customer IBAN (Buy-Out step 2) */}
          {flowCfg.showExtraAmount && (
            <div>
              <label className="block text-[#5F5F5F] text-sm mb-1">
                {translate("OTHER_SERVICES.EXTRA_AMOUNT")}
              </label>
              <Input
                value={extraAmount}
                onChange={(e) => setExtraAmount(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="00000"
                size="large"
                inputMode="numeric"
                prefix={<img src={SaudiRiyal} alt="" className="w-4 h-4" />}
              />
            </div>
          )}

          {flowCfg.showIban && (
            <div className="mt-4">
              <label className="block text-[#5F5F5F] text-sm mb-1">
                {translate("OTHER_SERVICES.ENTER_IBAN")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <Input
                value={iban}
                onChange={(e) => setIban(e.target.value.replace(/[^a-zA-Z0-9]/g, ""))}
                placeholder={translate("OTHER_SERVICES.IBAN_PLACEHOLDER")}
                size="large"
              />
            </div>
          )}

          {/* Tenure (restructure/topup only) */}
          {flowCfg.showTenure && (
            <>
              <p className="text-[#2F2F2F] text-base font-medium mt-6 mb-3">
                {translate("OTHER_SERVICES.PLEASE_SELECT_TENURE")}
              </p>
              <label className="block text-[#5F5F5F] text-sm mb-1">
                {translate("OTHER_SERVICES.TENURE_IN_MONTHS")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <Select
                value={tenure}
                onChange={setTenure}
                placeholder={translate("OTHER_SERVICES.TENURE_PLACEHOLDER")}
                className="w-full"
                size="large"
                options={TENURE_MONTHS.map((m) => ({
                  value: String(m),
                  label: `${m} ${translate("OTHER_SERVICES.MONTHS_SUFFIX")}`,
                }))}
              />
            </>
          )}

          {/* Topup amount (topup only) */}
          {flowCfg.showTopupAmount && (
            <div className="mt-4">
              <label className="block text-[#5F5F5F] text-sm mb-1">
                {translate("OTHER_SERVICES.TOPUP_AMOUNT")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <Input
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="00000"
                size="large"
                inputMode="numeric"
                prefix={<img src={SaudiRiyal} alt="" className="w-4 h-4" />}
              />
            </div>
          )}

          {/* Early Settlement amount — read-only, from the payoff-quote endpoint.
              Not part of the submit payload; the backend re-reads it from Temenos. */}
          {flowCfg.showEarlySettlementAmount && (
            <ReadOnlyField
              label={translate("OTHER_SERVICES.EARLY_SETTLEMENT_AMOUNT")}
              icon={<img src={SaudiRiyal} alt="" className="w-4 h-4" />}
            >
              {quoteLoading ? <LoadingOutlined /> : fmt(earlySettlementAmount)}
            </ReadOnlyField>
          )}

          {/* Partial settlement: repayment flavour, recalculation basis, instalments */}
          {flowCfg.showPartialFields && (
            <div className="mt-4 flex flex-col gap-4">
              <div>
                <label className="block text-[#5F5F5F] text-sm mb-1">
                  {translate("OTHER_SERVICES.REPAYMENT_TYPE")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <Select
                  value={repaymentType}
                  onChange={setRepaymentType}
                  placeholder={translate("OTHER_SERVICES.REPAYMENT_TYPE_PLACEHOLDER")}
                  className="w-full"
                  size="large"
                  options={PARTIAL_REPAYMENT_TYPES.map((o) => ({
                    value: o.value,
                    label: translate(o.labelKey),
                  }))}
                />
              </div>
              <div>
                <label className="block text-[#5F5F5F] text-sm mb-1">
                  {translate("OTHER_SERVICES.RECALCULATION_METHOD")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <Select
                  value={recalculationMethod}
                  onChange={setRecalculationMethod}
                  placeholder={translate("OTHER_SERVICES.RECALCULATION_PLACEHOLDER")}
                  className="w-full"
                  size="large"
                  options={PARTIAL_RECALCULATION_METHODS.map((o) => ({
                    value: o.value,
                    label: translate(o.labelKey),
                  }))}
                />
              </div>
              <div>
                <label className="block text-[#5F5F5F] text-sm mb-1">
                  {translate("OTHER_SERVICES.NO_OF_INSTALLMENTS")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <Input
                  value={noOfInstallment}
                  onChange={(e) => setNoOfInstallment(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="0"
                  size="large"
                  inputMode="numeric"
                />
              </div>
            </div>
          )}

          {/* Upload document (single PDF, V2 upload rules — restructure/topup only) */}
          {flowCfg.showDocumentUpload && (
            <DocumentUploadField
              label={translate("OTHER_SERVICES.UPLOAD_DOCUMENT")}
              hint={translate("OTHER_SERVICES.UPLOAD_DOC_HINT")}
              placeholder={translate("OTHER_SERVICES.DOCUMENT")}
              uploadText={translate("OTHER_SERVICES.UPLOAD")}
              file={documentFile}
              onPick={(file) => validateDocument(file) && setDocumentFile(file)}
            />
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-8">
            <Btn
              className="rounded-full bg-[#F0F0F0] text-[#2F2F2F] font-semibold px-10 py-5 border-0"
              onClick={handleBack}
            >
              {translate("OTHER_SERVICES.BACK")}
            </Btn>
            <Btn
              disabled={!canSubmit || submitting}
              className="rounded-full bg-primary text-white font-semibold px-10 py-5 border border-primary disabled:opacity-60 hover:!border-primary hover:border-solid hover:!text-primary"
              onClick={handleSubmit}
            >
              {submitting ? (
                <LoadingOutlined />
              ) : (
                translate("OTHER_SERVICES.SUBMIT")
              )}
            </Btn>
          </div>
        </>
      )}

      {successModal}
    </div>
  );
};

export default FinanceServiceFlow;
